
import { useState, useEffect } from 'react';

interface FloatingElementProps {
  children: React.ReactNode;
  delay?: number;
}

const FloatingElement = ({ children, delay = 0 }: FloatingElementProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`transition-all duration-1000 ease-out ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-4'
      }`}
      style={{
        animation: isVisible ? 'float 6s ease-in-out infinite' : 'none'
      }}
    >
      {children}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  );
};

interface PulseButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

const PulseButton = ({ children, onClick, className = '' }: PulseButtonProps) => {
  const [isPulsing, setIsPulsing] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsPulsing(true);
      setTimeout(() => setIsPulsing(false), 1000);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <button
      onClick={onClick}
      className={`relative overflow-hidden transition-all duration-300 hover:scale-105 cursor-pointer ${className} ${
        isPulsing ? 'animate-pulse' : ''
      }`}
    >
      {children}
      <div className="absolute inset-0 bg-white/20 opacity-0 hover:opacity-100 transition-opacity duration-300" />
    </button>
  );
};

interface HoverRevealProps {
  children: React.ReactNode;
  revealContent: React.ReactNode;
  className?: string;
}

const HoverReveal = ({ children, revealContent, className = '' }: HoverRevealProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`relative overflow-hidden cursor-pointer ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`transition-all duration-500 ease-out ${
          isHovered ? 'transform -translate-y-full opacity-0' : 'transform translate-y-0 opacity-100'
        }`}
      >
        {children}
      </div>
      <div
        className={`absolute inset-0 transition-all duration-500 ease-out ${
          isHovered ? 'transform translate-y-0 opacity-100' : 'transform translate-y-full opacity-0'
        }`}
      >
        {revealContent}
      </div>
    </div>
  );
};

interface CountUpProps {
  end: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
}

const CountUp = ({ end, duration = 2000, suffix = '', prefix = '' }: CountUpProps) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!hasStarted) return;

    const startTime = Date.now();
    const endTime = startTime + duration;

    const updateCount = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const easeOutProgress = 1 - Math.pow(1 - progress, 3);
      
      setCount(Math.floor(easeOutProgress * end));

      if (now < endTime) {
        requestAnimationFrame(updateCount);
      }
    };

    updateCount();
  }, [end, duration, hasStarted]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.5 }
    );

    const element = document.getElementById(`countup-${end}`);
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, [end, hasStarted]);

  return (
    <span id={`countup-${end}`}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

interface TypewriterProps {
  text: string;
  speed?: number;
  delay?: number;
}

const Typewriter = ({ text, speed = 50, delay = 0 }: TypewriterProps) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHasStarted(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    if (!hasStarted || currentIndex >= text.length) return;

    const timer = setTimeout(() => {
      setDisplayText(text.slice(0, currentIndex + 1));
      setCurrentIndex(currentIndex + 1);
    }, speed);

    return () => clearTimeout(timer);
  }, [currentIndex, text, speed, hasStarted]);

  return (
    <span>
      {displayText}
      <span className="animate-pulse">|</span>
    </span>
  );
};

interface ShakeOnHoverProps {
  children: React.ReactNode;
  className?: string;
}

const ShakeOnHover = ({ children, className = '' }: ShakeOnHoverProps) => {
  const [isShaking, setIsShaking] = useState(false);

  return (
    <div
      className={`transition-transform duration-100 cursor-pointer ${
        isShaking ? 'animate-bounce' : ''
      } ${className}`}
      onMouseEnter={() => setIsShaking(true)}
      onMouseLeave={() => setIsShaking(false)}
    >
      {children}
    </div>
  );
};

export {
  FloatingElement,
  PulseButton,
  HoverReveal,
  CountUp,
  Typewriter,
  ShakeOnHover
};
